"use client";

import Image from "next/image";
import {
  ArrowRight,
  ArrowUpRight,
  Cloud,
  Cpu,
  Gauge,
  Minus,
  Plus,
  Server,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

import { useChallengeModal } from "./challenge-modal";
import { BRAND_CYAN, Btn, Container, Eyebrow, SectionHead } from "./shared";

// ---------- Hero ----------
export const Hero = () => {
  const { open: openChallenge } = useChallengeModal();
  return (
  <section
    style={{ padding: "120px 0 100px", position: "relative", overflow: "hidden" }}
  >
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage:
          "radial-gradient(circle at 1px 1px, color-mix(in oklab, var(--foreground) 8%, transparent) 1px, transparent 0)",
        backgroundSize: "32px 32px",
        maskImage:
          "radial-gradient(ellipse 90% 60% at 50% 0%, #000 30%, transparent 75%)",
        WebkitMaskImage:
          "radial-gradient(ellipse 90% 60% at 50% 0%, #000 30%, transparent 75%)",
        pointerEvents: "none",
      }}
    />
    <Container style={{ position: "relative" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 28,
          maxWidth: 920,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px 8px 10px",
            borderRadius: 99,
            border: "1px solid var(--border)",
            background: "color-mix(in oklab, var(--background) 70%, transparent)",
          }}
        >
          <Image
            src="/md_logo.svg"
            alt="Moondream"
            width={20}
            height={20}
            unoptimized
          />
          <span
            style={{
              font: "600 14px/18px var(--font-sans)",
              letterSpacing: "-0.01em",
              color: "var(--foreground)",
            }}
          >
            Moondream Lens
            <span style={{ color: "var(--muted-foreground)", fontWeight: 500 }}>
              :
            </span>{" "}
            <span
              style={{
                font: "600 13px/18px var(--font-mono)",
                letterSpacing: "0.04em",
              }}
            >
              finetuning
            </span>
          </span>
        </div>
        <h1
          style={{
            font: "500 88px/92px 'Newsreader', Georgia, serif",
            letterSpacing: "-2.4px",
            margin: 0,
          }}
        >
          Moondream is good at a lot of things.{" "}
          <span style={{ fontStyle: "italic" }}>
            Fine-tune it to be great at yours.
          </span>
        </h1>
        <p
          style={{
            font: "400 20px/30px var(--font-sans)",
            color: "var(--muted-foreground)",
            margin: 0,
            maxWidth: 680,
          }}
        >
          Lens is Moondream&apos;s hosted fine-tuning API. Bring your task and labeled
          examples, Lens trains a Moondream variant that beats general-purpose
          VLMs on the work you actually care about. It runs anywhere from a
          Jetson at the edge to our cloud.
        </p>
        <div className="lens-cta-row" style={{ display: "flex", gap: 12, marginTop: 4 }}>
          <Btn variant="primary" size="lg" href="https://docs.moondream.ai">
            Read the docs
          </Btn>
          <Btn variant="outline" size="lg" onClick={openChallenge}>
            Try the 10-image challenge
          </Btn>
        </div>
        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexWrap: "wrap",
            gap: "10px 24px",
            font: "500 13px/18px var(--font-mono)",
            color: "var(--muted-foreground)",
            letterSpacing: "0.02em",
          }}
        >
          {["Open weights", "SFT and RL", "Edge to datacenter", "Cloud or on-device"].map(
            (t, i) => (
              <span
                key={t}
                style={{ display: "inline-flex", alignItems: "center", gap: 14 }}
              >
                {i > 0 ? (
                  <span
                    style={{
                      width: 3,
                      height: 3,
                      borderRadius: 99,
                      background: "var(--muted-foreground)",
                      display: "inline-block",
                      marginLeft: -10,
                    }}
                  />
                ) : null}
                {t}
              </span>
            )
          )}
        </div>
      </div>
    </Container>
  </section>
  );
};

// ---------- Methods ----------
type MethodCardProps = {
  tag: string;
  sub: string;
  headline: string;
  body: string;
  fits: string[];
  table: [string, string][];
};

const MethodCard = ({ tag, sub, headline, body, fits, table }: MethodCardProps) => (
  <div
    style={{
      border: "1px solid var(--border)",
      borderRadius: 14,
      background: "var(--card)",
      padding: 32,
      display: "flex",
      flexDirection: "column",
      gap: 20,
    }}
  >
    <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
      <span
        style={{
          font: "600 12px/16px var(--font-mono)",
          letterSpacing: "0.10em",
          padding: "6px 10px",
          borderRadius: 99,
          background: "var(--muted)",
          color: "var(--foreground)",
          border: "1px solid var(--border)",
        }}
      >
        {tag}
      </span>
      <span
        style={{
          font: "400 13px/18px var(--font-sans)",
          color: "var(--muted-foreground)",
        }}
      >
        {sub}
      </span>
    </div>
    <h3
      style={{
        font: "500 32px/40px 'Newsreader', Georgia, serif",
        letterSpacing: "-0.8px",
        margin: 0,
      }}
    >
      {headline}
    </h3>
    <p
      style={{
        font: "400 16px/26px var(--font-sans)",
        color: "var(--muted-foreground)",
        margin: 0,
        flex: "1 0 auto",
      }}
    >
      {body}
    </p>
    <div style={{ height: 1, background: "var(--border)" }} />
    <div>
      <div
        style={{
          font: "600 11px/16px var(--font-sans)",
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--muted-foreground)",
          marginBottom: 12,
        }}
      >
        Best fit for
      </div>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {fits.map((f) => (
          <li
            key={f}
            style={{
              display: "flex",
              gap: 10,
              font: "400 14px/22px var(--font-sans)",
              color: "var(--foreground)",
            }}
          >
            <span
              style={{
                marginTop: 8,
                width: 6,
                height: 6,
                borderRadius: 99,
                background: BRAND_CYAN,
                flex: "0 0 auto",
              }}
            />
            {f}
          </li>
        ))}
      </ul>
    </div>
    <div>
      <div
        style={{
          font: "600 11px/16px var(--font-sans)",
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--muted-foreground)",
          marginBottom: 8,
        }}
      >
        How much data
      </div>
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {table.map((r, i) => (
          <div
            key={r[0]}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              borderTop: i === 0 ? "none" : "1px solid var(--border)",
            }}
          >
            <div
              style={{
                padding: "10px 14px",
                font: "400 13px/20px var(--font-sans)",
                color: "var(--card-foreground)",
              }}
            >
              {r[0]}
            </div>
            <div
              style={{
                padding: "10px 14px",
                font: "500 13px/20px var(--font-mono)",
                color: "var(--foreground)",
                borderLeft: "1px solid var(--border)",
              }}
            >
              {r[1]}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const MethodsSection = () => (
  <section style={{ padding: "140px 0", background: "var(--secondary)" }}>
    <Container>
      <SectionHead
        eyebrow="Two ways to teach it"
        heading={
          <>
            Pick the method that fits{" "}
            <span style={{ fontStyle: "italic" }}>the data you have.</span>
          </>
        }
      />

      <div
        className="methods-grid"
        style={{
          marginTop: 56,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
        }}
      >
        <MethodCard
          tag="SFT"
          sub="Supervised fine-tuning"
          headline="Show, don't tell."
          body="Give Moondream input/output pairs and it learns to match them. Best for teaching domain-specific concepts or when you already have a dataset."
          fits={[
            "Classification with a small set of categories",
            "Captioning in a fixed style or voice",
            "Detection with bounding boxes",
            "Structured outputs and form parsing",
          ]}
          table={[
            ["Classification", "25 to 100 per class"],
            ["Captioning in a style", "100 to 500 examples"],
            ["Main cost", "Producing large data set."],
            ["Complex tasks", "1,000+"],
          ]}
        />

        <MethodCard
          tag="RL"
          sub="Reinforcement learning"
          headline="Reward what works."
          body="Give Moondream a task and score its answer variations. It learns which ones score higher. Best when the model is already somewhat proficient, or when you only have a few examples. Works with as few as 20."
          fits={[
            "Reasoning and multi-step tasks",
            "Open-ended outputs with many valid answers",
            "Cases where you can verify correctness automatically",
            "Optimizing directly for a metric",
          ]}
          table={[
            ["Classification", "5 to 20 per class"],
            ["Reasoning tasks", "100 to 500 prompts"],
            ["Open-ended", "Depends on reward quality"],
            ["Main cost", "Designing the scorer"],
          ]}
        />
      </div>

      <div
        style={{
          marginTop: 32,
          border: "1px solid var(--border)",
          borderRadius: 14,
          background: "var(--card)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid var(--border)",
            font: "600 11px/16px var(--font-sans)",
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: "var(--muted-foreground)",
          }}
        >
          Quick rule
        </div>
        {(
          [
            { k: "SFT", body: "if labeling is cheap." },
            {
              k: "SFT",
              body:
                "if you're teaching it new concepts, or using your own domain-specific language or concepts.",
            },
            { k: "RL", body: "if labeling is hard but checking is easy." },
            { k: "RL", body: "if you only have a small dataset." },
          ] as { k: "SFT" | "RL"; body: string }[]
        ).map((r, i) => {
          const sft = r.k === "SFT";
          return (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "120px 1fr",
                padding: "18px 24px",
                borderTop: i === 0 ? "none" : "1px solid var(--border)",
                alignItems: "center",
                gap: 16,
              }}
            >
              <span
                style={{
                  justifySelf: "start",
                  font: "600 13px/18px var(--font-mono)",
                  padding: "4px 10px",
                  borderRadius: 99,
                  background: sft
                    ? "color-mix(in oklab, #2563EB 12%, var(--background))"
                    : "color-mix(in oklab, #9B8AFB 18%, var(--background))",
                  border: `1px solid ${
                    sft
                      ? "color-mix(in oklab, #2563EB 35%, transparent)"
                      : "color-mix(in oklab, #9B8AFB 45%, transparent)"
                  }`,
                  color: sft ? "#2563EB" : "#5b4ed1",
                }}
              >
                {r.k}
              </span>
              <span
                style={{
                  font: "400 17px/26px var(--font-sans)",
                  color: "var(--foreground)",
                }}
              >
                {r.body}
              </span>
            </div>
          );
        })}
      </div>

      <p
        style={{
          marginTop: 20,
          textAlign: "center",
          font: "400 14px/22px var(--font-sans)",
          color: "var(--muted-foreground)",
        }}
      >
        Not sure?{" "}
        <a
          href="#challenge"
          style={{
            color: "var(--foreground)",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          Send 10 examples
        </a>{" "}
        and we&apos;ll tell you which method to use.
      </p>
    </Container>
  </section>
);

// ---------- Why Lens ----------
type PillarProps = {
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  head: string;
  body: string;
};

const Pillar = ({ Icon, head, body }: PillarProps) => (
  <div
    style={{
      padding: "40px 32px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
    }}
  >
    <span
      style={{
        width: 40,
        height: 40,
        borderRadius: 8,
        background: "var(--muted)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--foreground)",
      }}
    >
      <Icon size={20} strokeWidth={1.6} />
    </span>
    <h3
      style={{
        font: "500 28px/34px 'Newsreader', Georgia, serif",
        letterSpacing: "-0.7px",
        margin: 0,
      }}
    >
      {head}
    </h3>
    <p
      style={{
        font: "400 15px/24px var(--font-sans)",
        color: "var(--muted-foreground)",
        margin: 0,
        maxWidth: 380,
      }}
    >
      {body}
    </p>
  </div>
);

export const WhySection = () => {
  const pillars: PillarProps[] = [
    {
      Icon: Cloud,
      head: "Train without infrastructure.",
      body: "Send your data through the API. Get back a model. No GPUs to provision, no training scripts to babysit, no environments to keep in sync.",
    },
    {
      Icon: Server,
      head: "Run it in our cloud.",
      body: "Hosted inference on Moondream Cloud. Call your fine-tune from any endpoint, autoscaled, with the same SDK as the base model.",
    },
    {
      Icon: Cpu,
      head: "Or run it on device.",
      body: "Photon runs your fine-tune locally on a Jetson at the edge, a workstation on the factory floor, or an air-gapped server. No data leaves your network.",
    },
    {
      Icon: Gauge,
      head: "Small enough to be fast.",
      body: "Moondream models are small by design. Real-time inference at low cost. Hundreds of inferences per second on a single GPU with Photon.",
    },
  ];

  return (
    <section style={{ padding: "140px 0" }}>
      <Container>
        <SectionHead
          eyebrow="Why Lens"
          heading={
            <>
              One fine-tune,{" "}
              <span style={{ fontStyle: "italic" }}>that runs everywhere.</span>
            </>
          }
          subhead="Closed APIs lock your fine-tune to their endpoint. Open frameworks make you build the training stack yourself. Lens trains the model for you, then lets you serve it from our cloud or run it on your own hardware with Photon."
        />

        <div
          className="why-grid"
          style={{
            marginTop: 56,
            border: "1px solid var(--border)",
            borderRadius: 14,
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            background: "var(--card)",
          }}
        >
          {pillars.map((p, i) => (
            <div
              key={p.head}
              className="why-cell"
              data-i={i}
              style={{
                borderTop: i > 1 ? "1px solid var(--border)" : "none",
                borderLeft: i % 2 === 1 ? "1px solid var(--border)" : "none",
              }}
            >
              <Pillar {...p} />
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

// ---------- 10-image challenge ----------
export const ChallengeSection = () => {
  const { open: openChallenge } = useChallengeModal();
  const steps = [
    {
      n: "01",
      h: "Pick a skill.",
      b: "Query, caption, detect, point, or segment.",
    },
    {
      n: "02",
      h: "Collect examples.",
      b: "10 to 50 labeled examples of your task. More if SFT, fewer if RL.",
    },
    {
      n: "03",
      h: "Call the API.",
      b: "Pass your data. Get a model back. Deploy it through Photon or run it locally.",
    },
  ];
  return (
    <section
      id="challenge"
      style={{ padding: "140px 0", background: "var(--secondary)" }}
    >
      <Container>
        <Eyebrow>The 10-image challenge</Eyebrow>
        <div
          className="challenge-grid"
          style={{
            marginTop: 28,
            display: "grid",
            gridTemplateColumns: "1.15fr 1fr",
            gap: 56,
            alignItems: "start",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <h2
              style={{
                font: "500 64px/68px 'Newsreader', Georgia, serif",
                letterSpacing: "-1.6px",
                margin: 0,
              }}
            >
              Bring your hardest task.{" "}
              <span style={{ fontStyle: "italic" }}>We&apos;ll prove it works.</span>
            </h2>
            <p
              style={{
                font: "400 18px/28px var(--font-sans)",
                color: "var(--muted-foreground)",
                margin: 0,
                maxWidth: 520,
              }}
            >
              Send us 10 labeled examples of your task. We will return a
              fine-tuned Moondream that does it better than the base model. If
              it does not, you owe us nothing.
            </p>
            <div className="lens-cta-row" style={{ display: "flex", gap: 12, marginTop: 4 }}>
              <Btn variant="primary" size="lg" onClick={openChallenge}>
                Submit your 10 images
              </Btn>
              <Btn variant="outline" size="lg">
                Talk to our team
              </Btn>
            </div>
          </div>
          <div
            style={{
              border: "1px solid var(--border)",
              borderRadius: 14,
              background: "var(--card)",
              padding: 28,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                font: "500 12px/16px var(--font-mono)",
                color: "var(--muted-foreground)",
                letterSpacing: "0.04em",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 6,
                  height: 6,
                  borderRadius: 99,
                  background: BRAND_CYAN,
                  marginRight: 8,
                  verticalAlign: "middle",
                }}
              />
              Or do it yourself
            </div>
            {steps.map((s, i) => (
              <div
                key={s.n}
                style={{
                  display: "grid",
                  gridTemplateColumns: "44px 1fr",
                  gap: 16,
                  padding: "18px 0",
                  borderTop: "1px solid var(--border)",
                  marginTop: i === 0 ? 12 : 0,
                  alignItems: "start",
                }}
              >
                <span
                  style={{
                    font: "500 13px/20px var(--font-mono)",
                    color: "var(--muted-foreground)",
                  }}
                >
                  {s.n}
                </span>
                <div>
                  <div
                    style={{
                      font: "600 16px/22px var(--font-sans)",
                      color: "var(--foreground)",
                    }}
                  >
                    {s.h}
                  </div>
                  <div
                    style={{
                      font: "400 14px/22px var(--font-sans)",
                      color: "var(--muted-foreground)",
                      marginTop: 4,
                    }}
                  >
                    {s.b}
                  </div>
                </div>
              </div>
            ))}
            <a
              href="https://docs.moondream.ai"
              style={{
                marginTop: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                font: "500 14px/22px var(--font-sans)",
                color: "var(--foreground)",
                textDecoration: "none",
              }}
            >
              Read the docs <ArrowRight size={14} strokeWidth={2} />
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
};

// ---------- Showcase ----------
export const ShowcaseSection = () => {
  const cards = [
    {
      tag: "Detect · SFT",
      h: "Player with Ball Detection",
      b: "Detect the player holding the basketball in NBA broadcast footage.",
      m: "F1 0.28 → 0.79",
      slug: "player-with-ball",
    },
    {
      tag: "Detect · SFT",
      h: "State Farm Logo Detection",
      b: "Detect State Farm logos in NBA broadcast frames.",
      m: "F1 0.38 → 1.00",
      slug: "state-farm-logo",
    },
    {
      tag: "Query · SFT",
      h: "GeoGuessr Countries",
      b: "Predict the country from a single street-view image.",
      m: "28.6% → 71.1%",
      slug: "geoguessr-countries",
    },
    {
      tag: "Query · RL",
      h: "Rock Paper Scissors",
      b: "Classify hand gestures with 5 examples per class.",
      m: "54.8% → 98.8%",
      slug: "rock-paper-scissors",
    },
    {
      tag: "Query · RL",
      h: "Glaucoma Detection",
      b: "Classify retinal images by glaucoma stage.",
      m: "17.6% → 69.2%",
      slug: "glaucoma-detection",
    },
    {
      tag: "Point · SFT",
      h: "Computer Use",
      b: "Click the correct UI element from a screenshot and instruction.",
      m: "27.0% → 63.8%",
      slug: "computer-use",
    },
  ];
  return (
    <section style={{ padding: "140px 0" }}>
      <Container>
        <SectionHead
          eyebrow="Real fine-tunes"
          heading="Example fine-tunes based on real customer use cases."
        />
        <div
          className="showcase-grid"
          style={{
            marginTop: 48,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
          }}
        >
          {cards.map((c) => (
            <a
              key={c.h}
              href={`/p/lens/${c.slug}`}
              className="lens-showcase-card"
              style={{
                border: "1px solid var(--border)",
                borderRadius: 12,
                background: "var(--card)",
                padding: 24,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                minHeight: 220,
                color: "inherit",
                textDecoration: "none",
                transition:
                  "background 200ms cubic-bezier(0.2,0.8,0.2,1), border-color 200ms",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    font: "500 12px/16px var(--font-mono)",
                    color: "var(--muted-foreground)",
                    letterSpacing: "0.02em",
                  }}
                >
                  {c.tag}
                </span>
                <ArrowUpRight size={16} strokeWidth={1.6} />
              </div>
              <h3
                style={{
                  font: "500 24px/30px 'Newsreader', Georgia, serif",
                  letterSpacing: "-0.6px",
                  margin: 0,
                }}
              >
                {c.h}
              </h3>
              <p
                style={{
                  font: "400 14px/22px var(--font-sans)",
                  color: "var(--muted-foreground)",
                  margin: 0,
                }}
              >
                {c.b}
              </p>
              <div
                style={{
                  marginTop: "auto",
                  paddingTop: 16,
                  borderTop: "1px solid var(--border)",
                  font: "500 14px/22px var(--font-mono)",
                  color: "var(--foreground)",
                }}
              >
                {c.m}
              </div>
            </a>
          ))}
        </div>
      </Container>
    </section>
  );
};

// ---------- FAQ ----------
export const FAQSection = () => {
  const items = [
    {
      q: "Do I need labeled data?",
      a: "For SFT, yes. For RL, you need a way to score outputs, which can replace labels. If you can verify correctness automatically (a regex, a scoring function, an external check), RL works without labeled examples.",
    },
    {
      q: "How much data do I need?",
      a: "Less than you would guess, especially with RL. We have seen RL fine-tunes succeed with as few as 5 examples per class. SFT typically wants 25 to 100 per class. If you have a tricky task and aren't sure where to start, send us 10 examples and we will tell you whether you have enough, what to label next, and which method to use.",
    },
    {
      q: "Should I use RL or SFT?",
      a: "If labeling is cheap, use SFT. If labeling is hard but checking is easy, use RL. If you have under 20 examples total and a way to verify correctness, RL is usually the answer.",
    },
    {
      q: "How long does training take?",
      a: "Most fine-tunes complete in minutes to a few hours. Small SFT jobs finish in under 10 minutes. RL runs depend on the size of the rollout pool and the complexity of the task.",
    },
    {
      q: "What if the results aren't good enough?",
      a: "Tell us. We will look at your data, your evaluation, and the training run. Sometimes it is a labeling issue, sometimes a method choice, sometimes a base model gap. We work with you until it ships.",
    },
    {
      q: "How do you handle my training data?",
      a: "Your data stays yours. We do not train shared models on it. We do not retain it longer than needed to run the job. Enterprise contracts include explicit data handling terms.",
    },
    {
      q: "How can I use my fine-tune locally?",
      a: "Every fine-tune is a Moondream model file you can download and run anywhere Moondream runs. That includes Photon for production deployments, the Hugging Face Transformers stack, and edge devices like Jetson.",
    },
  ];
  const [open, setOpen] = useState<number>(0);
  return (
    <section style={{ padding: "140px 0", background: "var(--secondary)" }}>
      <Container>
        <SectionHead eyebrow="Frequently asked" heading="Questions, answered." />
        <div
          style={{
            marginTop: 48,
            border: "1px solid var(--border)",
            borderRadius: 14,
            background: "var(--card)",
            overflow: "hidden",
          }}
        >
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div
                key={it.q}
                style={{ borderTop: i === 0 ? "none" : "1px solid var(--border)" }}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "22px 28px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    font: "500 18px/26px var(--font-sans)",
                    letterSpacing: "-0.005em",
                    color: "var(--foreground)",
                  }}
                >
                  <span>{it.q}</span>
                  {isOpen ? (
                    <Minus size={18} strokeWidth={1.6} />
                  ) : (
                    <Plus size={18} strokeWidth={1.6} />
                  )}
                </button>
                {isOpen ? (
                  <div style={{ padding: "0 28px 26px 28px", maxWidth: 760 }}>
                    <p
                      style={{
                        font: "400 16px/26px var(--font-sans)",
                        color: "var(--muted-foreground)",
                        margin: 0,
                      }}
                    >
                      {it.a}
                    </p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

// ---------- Final CTA ----------
export const FinalCTA = () => (
  <section style={{ padding: "160px 0" }}>
    <Container>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 32,
        }}
      >
        <h2
          style={{
            font: "500 88px/92px 'Newsreader', Georgia, serif",
            letterSpacing: "-2.2px",
            margin: 0,
            maxWidth: 1000,
          }}
        >
          Ready to take Moondream
          <br />
          <span style={{ fontStyle: "italic" }}>to production?</span>
        </h2>
        <div className="lens-cta-row" style={{ display: "flex", gap: 12 }}>
          <Btn variant="primary" size="lg">
            Start fine-tuning
          </Btn>
          <Btn variant="outline" size="lg">
            Talk to our team
          </Btn>
        </div>
      </div>
    </Container>
  </section>
);

// Helper export so we can refer to ReactNode types externally if needed
export type { ReactNode };
